import React, { useEffect, useState, useCallback } from 'react';
import { Dropdown } from '@/components/ui/dropdown';
import axios from '../utils/axiosInstance';
import { Button } from "@/components/ui/button";
import { PageLoader } from '@/components/ui/page-loader';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { toast } from 'sonner';
import { Trash2, Eye, Dot, ChevronDown, Star, Lock, Crown, BellRing, Monitor, Smartphone, MapPin, RotateCcw, Clock, Music, Activity } from 'lucide-react';

// Read user_type from storage to gate super-admin-only actions (e.g. lead deletion).
function readUserTypeFromStorage(): string | null {
  try {
    const raw = localStorage.getItem('user') || sessionStorage.getItem('user');
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed?.user_type || null;
  } catch {
    return null;
  }
}
import { Pagination } from '@/components/ui/pagination';
import { usePagination } from '@/hooks/usePagination';
import { usePlan } from '@/context/PlanContext';
import { GracePeriodBanner } from '@/components/PlanComponents';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import ScoreRing from '@/components/lead-detail/ScoreRing';
import JourneyFlow from '@/components/lead-detail/JourneyFlow';
import InfoRow from '@/components/lead-detail/InfoRow';
import InteractionList from '@/components/lead-detail/InteractionList';

interface LeadIntelligence {
  leadId: string;
  intent?: {
    type: 'booking' | 'collaboration' | 'general';
    score: number;
    signals: string[];
  };
  sessionSummary?: {
    durationSeconds: number;
    pagesVisited: string[];
    totalEvents: number;
    songsPlayed: string[];
    songPlayCounts: Record<string, number>;
    totalSongsPlayed: number;
    imagesViewed: string[];
    eventsViewed: string[];
    socialClicks: Record<string, number>;
    streamingClicks: Record<string, number>;
    eventBreakdown: Record<string, number>;
    sectionsVisited: string[];
    sectionEngagement: Record<string, number>;
    sectionFlow: string[];
    entryPage: string | null;
    entrySection: string | null;
    exitSection: string | null;
  };
  isRepeatLead?: boolean;
  country?: string;
  city?: string;
  org?: string;
  device?: string;
  browser?: string;
  os?: string;
  referrer?: string;
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  sessionId?: string;
}

interface ContactSubmission {
  _id: string;
  fullName: string;
  email: string;
  mobileNumber: string;
  purposeOfContact: string;
  message: string;
  ipAddress: string;
  userAgent: string;
  geolocation?: {
    country?: string;
    region?: string;
    city?: string;
  };
  submittedAt: string;
  isImportant?: boolean;
  isOpened?: boolean;
  leadIntelligence?: LeadIntelligence;
}

interface ContactWebsiteManagerProps {
  domain: string;
  onUnopendedCountChange?: (count: number) => void;
}

const ContactWebsiteManager: React.FC<ContactWebsiteManagerProps> = ({ domain, onUnopendedCountChange }) => {
  const { hasContactLeadsAccess, openUpgradeModal } = usePlan();
  const isSuperAdmin = readUserTypeFromStorage() === 'super-admin';
  const [submissions, setSubmissions] = useState<ContactSubmission[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedSubmission, setSelectedSubmission] = useState<ContactSubmission | null>(null);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedPurpose, setSelectedPurpose] = useState<string>('');
  const [fromDate, setFromDate] = useState<string>('');
  const [toDate, setToDate] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'unread' | 'important' | 'highIntent'>('all');
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);

  const [emailNotificationsEnabled, setEmailNotificationsEnabled] = useState(true);
  const [togglingEmailNotifications, setTogglingEmailNotifications] = useState(false);

  // Track which submission is currently being toggled as important (for button feedback)
  const [togglingImportantId, setTogglingImportantId] = useState<string | null>(null);

  // Lead intelligence state
  const [stats, setStats] = useState({ total: 0, unopened: 0, highIntent: 0, bookingIntent: 0 });
  // const [eventHistory, setEventHistory] = useState<EventItem[]>([]);
  // const [detailLoading, setDetailLoading] = useState(false);
  // const [showTimeline, setShowTimeline] = useState(false);

  // Available purposes
  const purposes = [
    'To work on a project',
    'To perform at an event',
    'To collaborate',
    'To promote/endorse a brand',
    'To do an appearance for televised show or an event',
    'Other'
  ];

  const fetchSubmissions = () => {
    setLoading(true);
    axios.get(`/api/contact-website/${domain}`)
      .then(res => setSubmissions(res.data))
      .catch(err => {
        console.error('Error fetching enquiries:', err);
        toast.error('Failed to load enquiries');
      })
      .finally(() => setLoading(false));
  };

  const fetchStats = useCallback(() => {
    if (!domain) return;
    axios.get(`/api/contact-website/${domain}/stats/summary`)
      .then(res => {
        if (res.data.stats) setStats(res.data.stats);
      })
      .catch(err => console.error('Error fetching lead stats:', err));
  }, [domain]);

  // Fetch email notification setting from domain
  const fetchEmailNotificationSetting = async () => {
    try {
      const response = await axios.get('/api/domain/plan-info');
      if (response.data && typeof response.data.leadEmailNotificationsEnabled === 'boolean') {
        setEmailNotificationsEnabled(response.data.leadEmailNotificationsEnabled);
      }
    } catch (err) {
      console.error('Error fetching email notification setting:', err);
    }
  };

  // Toggle email notifications
  const handleToggleEmailNotifications = async () => {
    setTogglingEmailNotifications(true);
    try {
      const response = await axios.patch('/api/domain/toggle-lead-notifications');
      setEmailNotificationsEnabled(response.data.leadEmailNotificationsEnabled);
      toast.success(response.data.message);
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || 'Failed to update email notification setting';
      toast.error(errorMessage);
    } finally {
      setTogglingEmailNotifications(false);
    }
  };

  useEffect(() => {
    if (domain) {
      fetchSubmissions();
      fetchStats();
      fetchEmailNotificationSetting();
    }
  }, [domain]);

  // Toggle important status
  const handleToggleImportant = async (submissionId: string) => {
    setTogglingImportantId(submissionId);
    try {
      const response = await axios.patch(`/api/contact-website/${submissionId}/important`);
      if (response.data.success) {
        // Update the submission in local state
        const updatedSubmissions = submissions.map(s => 
          s._id === submissionId 
            ? { ...s, isImportant: response.data.isImportant }
            : s
        );
        setSubmissions(updatedSubmissions);
        
        // Update selected submission if it's the one being toggled
        if (selectedSubmission && selectedSubmission._id === submissionId) {
          setSelectedSubmission({ ...selectedSubmission, isImportant: response.data.isImportant });
        }
        
        toast.success(response.data.message);
      }
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || 'Failed to update enquiry';
      toast.error(errorMessage);
    } finally {
      setTogglingImportantId(null);
    }
  };

  // Calculate and notify parent of unopened count
  useEffect(() => {
    const unopenedCount = submissions.filter(s => !s.isOpened).length;
    onUnopendedCountChange?.(unopenedCount);
  }, [submissions, onUnopendedCountChange]);

  const handleDelete = async () => {
    if (!deleteId) return;

    try {
      await axios.delete(`/api/contact-website/${deleteId}`);
      setSubmissions(submissions.filter(s => s._id !== deleteId));
      setDeleteId(null);
      toast.success('Enquiry deleted successfully!');
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || 'Failed to delete enquiry';
      toast.error(errorMessage);
    }
  };

  const handleExport = () => {
    const csv = [
      ['Full Name', 'Email', 'Mobile Number', 'Purpose', 'Intent', 'Score', 'Message', 'Location', 'Submitted At'],
      ...submissions.map(s => [
        s.fullName,
        s.email,
        s.mobileNumber,
        s.purposeOfContact,
        s.leadIntelligence?.intent?.type || '-',
        String(s.leadIntelligence?.intent?.score || 0),
        s.message,
        s.leadIntelligence?.city 
          ? [s.leadIntelligence.city, s.leadIntelligence.country].filter(Boolean).join(', ')
          : `${s.geolocation?.city || '-'}, ${s.geolocation?.country || '-'}`,
        new Date(s.submittedAt).toLocaleString()
      ])
    ].map(row => row.map(cell => `"${cell}"`).join(',')).join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `contact-enquiries-${domain}-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  // Derive a short type label from purposeOfContact
  const getPurposeType = (purpose: string): string => {
    if (/perform|event|appearance|televised/i.test(purpose)) return 'Booking';
    if (/collaborate|project|work on/i.test(purpose)) return 'Collab';
    if (/promote|endorse|brand/i.test(purpose)) return 'Brand';
    return 'General';
  };

  const filteredSubmissions = submissions.filter(s => {
    // Search filter - name or email
    const matchesSearch = s.fullName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      s.email.toLowerCase().includes(searchQuery.toLowerCase());
    
    // Purpose filter
    const matchesPurpose = !selectedPurpose || s.purposeOfContact === selectedPurpose;
    
    // Date range filter
    const submissionDate = new Date(s.submittedAt).toISOString().split('T')[0];
    const matchesDateRange = (!fromDate || submissionDate >= fromDate) && 
                            (!toDate || submissionDate <= toDate);
    
    // Read status filter - use database isOpened field
    const isOpened = s.isOpened || false;
    const isImportant = s.isImportant || false;
    
    let matchesStatusFilter = false;
    if (statusFilter === 'all') {
      matchesStatusFilter = true;
    } else if (statusFilter === 'unread') {
      matchesStatusFilter = !isOpened;
    } else if (statusFilter === 'important') {
      matchesStatusFilter = isImportant;
    } else if (statusFilter === 'highIntent') {
      matchesStatusFilter = (s.leadIntelligence?.intent?.score || 0) >= 50;
    }
    
    return matchesSearch && matchesPurpose && matchesDateRange && matchesStatusFilter;
  }).sort((a, b) => {
    const dateA = new Date(a.submittedAt).getTime();
    const dateB = new Date(b.submittedAt).getTime();
    return dateB - dateA; // Always latest first
  });

  // Pagination
  const { paginate, paginationProps } = usePagination(filteredSubmissions.length, {
    resetDeps: [searchQuery, selectedPurpose, fromDate, toDate, statusFilter],
  });
  const paginatedSubmissions = paginate(filteredSubmissions);

  if (!domain) {
    return (
      <div className="p-8 text-center text-muted-foreground">
        Please select a domain to view contact enquiries
      </div>
    );
  }

  // Show upgrade overlay when the current plan lacks Leads access
  if (!hasContactLeadsAccess) {
    return (
      <div className="relative">
        <GracePeriodBanner />
        
        {/* Blurred preview background */}
        <div className="filter blur-sm opacity-50 pointer-events-none">
          <div className="mb-8">
            <h1 className="text-lg font-semibold text-foreground mb-2">Website Leads</h1>
            <p className="text-sm text-muted-foreground">Manage enquiries with AI-powered lead intelligence</p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
            {['Total Enquiries', 'Unopened', 'High Intent', 'Booking Intent'].map(label => (
              <div key={label} className="bg-card rounded-lg border border-border p-4">
                <div className="text-muted-foreground text-xs sm:text-sm font-medium mb-2">{label}</div>
                <div className="text-2xl sm:text-3xl font-bold text-foreground">--</div>
              </div>
            ))}
          </div>
        </div>

        {/* Upgrade overlay */}
        <div className="absolute inset-0 flex items-center justify-center bg-background/80 backdrop-blur-sm rounded-lg">
          <div className="text-center max-w-md mx-auto p-8">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-amber-100 dark:bg-amber-900/30 mb-6">
              <Lock className="w-8 h-8 text-amber-600 dark:text-amber-400" />
            </div>
            <h2 className="text-xl sm:text-2xl font-bold text-foreground mb-3">Website Leads Locked</h2>
            <p className="text-muted-foreground mb-6">
              Access to website leads and contact form submissions is available on Pro Max. Upgrade to unlock this feature and never miss a potential client!
            </p>
            <div className="bg-muted/50 rounded-lg p-4 mb-6 text-left">
              <h3 className="font-medium text-foreground mb-2 flex items-center gap-2">
                <Crown className="w-4 h-4 text-amber-500" />
                Pro Max Benefits
              </h3>
              <ul className="text-sm text-muted-foreground space-y-1">
                <li>✓ View all website enquiries</li>
                <li>✓ AI-powered lead scoring & intent detection</li>
                <li>✓ Visitor journey tracking</li>
                <li>✓ Mark leads as important & export data</li>
              </ul>
            </div>
            <Button 
              onClick={() => openUpgradeModal('contactLeadsAccess')}
              className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white"
            >
              <Crown className="w-4 h-4 mr-2" />
              Upgrade to Pro Max
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="">

      {/* Header */}
      <div className="mb-5 sm:mb-8 flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
        <div>
          <h1 className="text-lg font-semibold text-foreground mb-2">Website Leads</h1>
          <p className="text-sm text-muted-foreground">Manage enquiries with AI-powered lead intelligence</p>
        </div>
        
        {/* Email Notification Toggle */}
        <div className="flex items-center gap-3 bg-card border border-border rounded-lg px-4 py-3">
          <BellRing className="w-4 h-4 text-muted-foreground" />
          <div className="flex flex-col">
            <span className="text-sm font-medium text-foreground">Email Notifications</span>
            <span className="text-xs text-muted-foreground">
              {emailNotificationsEnabled ? 'Leads sent to your email' : 'Email notifications off'}
            </span>
          </div>
          <Switch
            checked={emailNotificationsEnabled}
            onCheckedChange={handleToggleEmailNotifications}
            disabled={togglingEmailNotifications}
          />
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 mb-6 sm:mb-8">
        <div className="bg-card rounded-lg border border-border p-4">
          <div className="text-muted-foreground text-xs sm:text-sm font-medium mb-1 sm:mb-2">Total Enquiries</div>
          <div className="text-2xl sm:text-3xl font-bold text-foreground">{stats.total}</div>
        </div>
        <div className="bg-card rounded-lg border border-border p-4">
          <div className="text-muted-foreground text-xs sm:text-sm font-medium mb-1 sm:mb-2">Unopened</div>
          <div className="text-2xl sm:text-3xl font-bold text-info">{stats.unopened}</div>
        </div>
        <div className="bg-card rounded-lg border border-border p-4">
          <div className="text-muted-foreground text-xs sm:text-sm font-medium mb-1 sm:mb-2">High Intent (50+)</div>
          <div className="text-2xl sm:text-3xl font-bold text-success">{stats.highIntent}</div>
        </div>
        <div className="bg-card rounded-lg border border-border p-4">
          <div className="text-muted-foreground text-xs sm:text-sm font-medium mb-1 sm:mb-2">Booking Intent</div>
          <div className="text-2xl sm:text-3xl font-bold text-warning">{stats.bookingIntent}</div>
        </div>
      </div>

      {/* Controls */}
      <div className="bg-card rounded-lg border border-border p-4 mb-6 space-y-4">
        {/* Search Bar */}
        <Input
          placeholder="Search by name or email..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="w-full"
        />
        
        {/* Primary Controls Row: Status Filter and Action Buttons */}
        <div className="flex flex-wrap items-center gap-3">
          {/* Status Filter Dropdown */}
          <Dropdown
            options={[
              { value: 'all', label: 'All' },
              { value: 'unread', label: '● Unread' },
              { value: 'important', label: '★ Important' },
              { value: 'highIntent', label: '⚡ High Intent' },
            ]}
            value={statusFilter}
            onValueChange={(val) => setStatusFilter(val as typeof statusFilter)}
            placeholder="All"
            triggerClassName="w-auto rounded-lg"
            panelClassName="min-w-36"
          />

          {/* Advanced Filters Toggle */}
          <button
            onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 ${
              showAdvancedFilters
                ? 'bg-secondary text-foreground hover:bg-accent'
                : 'bg-secondary text-foreground hover:bg-accent'
            }`}
          >
            Filters
            <ChevronDown size={16} className={`transition-transform ${showAdvancedFilters ? 'rotate-180' : ''}`} />
          </button>

          {/* Action Buttons */}
          <div className="flex gap-2 ml-auto">
            <Button onClick={handleExport} variant="outline" size="sm">
              Export CSV
            </Button>
            <Button onClick={() => { fetchSubmissions(); fetchStats(); }} variant="outline" size="sm">
              Refresh
            </Button>
          </div>
        </div>

        {/* Advanced Filters Section */}
        {showAdvancedFilters && (
          <div className="border-t border-border pt-4 space-y-3">
            <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Advanced Filters</div>
            <div className="flex flex-wrap items-center gap-3">
              {/* Purpose Filter */}
              <div className="flex-1 min-w-0 sm:min-w-48">
                <Dropdown
                  options={[
                    { value: '', label: 'All Purposes' },
                    ...purposes.map(purpose => ({ value: purpose, label: purpose }))
                  ]}
                  value={selectedPurpose}
                  onValueChange={(val) => setSelectedPurpose(val as string)}
                  placeholder="All Purposes"
                  panelClassName="w-max max-w-100"
                />
              </div>

              {/* Date From Filter */}
              <div className="flex-1 min-w-40">
                <Input
                  type="date"
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                  placeholder="From Date"
                  className="appearance-none scheme-light dark:scheme-dark"
                />
              </div>

              {/* Date To Filter */}
              <div className="flex-1 min-w-40">
                <Input
                  type="date"
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                  placeholder="To Date"
                  className="appearance-none scheme-light dark:scheme-dark"
                />
              </div>

              {/* Clear Filters */}
              <button
                onClick={() => {
                  setSelectedPurpose('');
                  setFromDate('');
                  setToDate('');
                }}
                className="px-3 py-2 rounded-lg text-sm font-medium bg-secondary text-foreground hover:bg-accent transition-colors"
              >
                Clear
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Table */}
      <div>
        {loading ? (
          <PageLoader label="Loading enquiries..." />
        ) : filteredSubmissions.length === 0 ? (
          <div className="p-8 text-center text-muted-foreground">
            {searchQuery ? 'No enquiries match your search' : 'No enquiries yet'}
          </div>
        ) : (
          <>
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/50">
                  <TableHead>Name</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Intent</TableHead>
                  <TableHead>Signals</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead className="w-24">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {paginatedSubmissions.map(submission => {
                const isOpened = submission.isOpened || false;
                const isImportant = submission.isImportant || false;
                const submissionDate = new Date(submission.submittedAt);
                const dateStr = submissionDate.toLocaleDateString();
                const intel = submission.leadIntelligence;
                const intentType = intel?.intent?.type || 'general';
                const emailDomain = submission.email.split('@')[1] || '';
                const purposeType = getPurposeType(submission.purposeOfContact);
                return (
                  <TableRow 
                    key={submission._id} 
                    className={`hover:bg-muted/50 transition-colors ${
                      isImportant
                        ? 'bg-warning-light'
                        : isOpened 
                        ? 'bg-card' 
                        : 'bg-info-light'
                    }`}
                  >
                    <TableCell className={`font-medium ${isOpened ? 'text-foreground' : 'font-semibold text-info'} ${
                      isImportant
                        ? 'border-l-4 border-l-warning'
                        : !isOpened
                        ? 'border-l-4 border-l-info'
                        : ''
                    }`}>
                      <div className="flex items-center gap-2">
                        {isImportant && <Star size={18} className="text-warning fill-warning flex-shrink-0" />}
                        {!isOpened && <Dot size={20} className="text-info fill-info flex-shrink-0" />}
                        <div>
                          <div>{submission.fullName}</div>
                          <div className="text-xs text-muted-foreground font-normal">{emailDomain}</div>
                          {intel?.isRepeatLead && (
                            <span className="text-xs text-warning font-normal">Repeat visitor</span>
                          )}
                          {(() => {
                            const hints: string[] = [];
                            const ss = intel?.sessionSummary;
                            if (ss?.songsPlayed && ss.songsPlayed.length > 0) {
                              const unique = [...new Set(ss.songsPlayed)];
                              hints.push(`Played ${unique.length} song${unique.length !== 1 ? 's' : ''}`);
                            }
                            if (ss?.eventBreakdown?.['contact_form_submit'] || (ss?.sectionFlow && ss.sectionFlow.some(s => s.toLowerCase() === 'contact'))) {
                              hints.push('Contacted');
                            }
                            return hints.length > 0 ? (
                              <div className="text-[11px] text-muted-foreground font-normal">{hints.join(' • ')}</div>
                            ) : null;
                          })()}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                        purposeType === 'Booking' ? 'bg-success-light text-success' :
                        purposeType === 'Collab' ? 'bg-info-light text-info' :
                        purposeType === 'Brand' ? 'bg-warning-light text-warning' :
                        'bg-secondary text-muted-foreground'
                      }`}>
                        {purposeType}
                      </span>
                    </TableCell>
                    <TableCell>
                      {intel?.intent ? (
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                          intentType === 'booking' ? 'bg-success-light text-success' :
                          intentType === 'collaboration' ? 'bg-info-light text-info' :
                          'bg-secondary text-muted-foreground'
                        }`}>
                          {intentType}
                        </span>
                      ) : (
                        <span className="text-xs text-muted-foreground">-</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {intel?.intent && intel.intent.signals.length > 0 ? (
                        <div className="flex flex-col gap-1">
                          {intel.intent.signals.slice(0, 2).map((signal, i) => (
                            <span key={i} className="text-xs text-muted-foreground truncate max-w-[180px]" title={signal}>
                              {signal}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <span className="text-xs text-muted-foreground">-</span>
                      )}
                    </TableCell>
                    <TableCell className={`text-sm ${isOpened ? 'text-muted-foreground' : 'text-info'}`}>
                      {dateStr}
                    </TableCell>
                    <TableCell className="flex gap-2">
                      <Button
                        size="icon-sm"
                        variant="outline"
                        onClick={async () => {
                          setSelectedSubmission(submission);
                          
                          // Mark as opened in backend if not already
                          if (!submission.isOpened) {
                            try {
                              await axios.patch(`/api/contact-website/${submission._id}/opened`);
                              setSubmissions(prev => 
                                prev.map(s => s._id === submission._id ? { ...s, isOpened: true } : s)
                              );
                            } catch (err) {
                              console.error('Error marking submission as opened:', err);
                            }
                          }
                        }}
                      >
                        <Eye size={16} />
                      </Button>
                      {isSuperAdmin && (
                        <Button
                          size="icon-sm"
                          variant="destructive"
                          onClick={() => setDeleteId(submission._id)}
                        >
                          <Trash2 size={16} />
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>

            {/* Pagination Controls */}
            <Pagination
              {...paginationProps}
              itemLabel="enquiries"
            />
          </>
        )}
      </div>

      {/* View Details Modal */}
      {selectedSubmission && (() => {
        const intel = selectedSubmission.leadIntelligence;
        const intentScore = intel?.intent?.score || 0;
        const intentType = intel?.intent?.type || 'general';
        const session = intel?.sessionSummary;
        // const emailDomain = selectedSubmission.email.split('@')[1] || '';
        const formatDuration = (sec: number) => {
          if (sec < 60) return `${sec}s`;
          const m = Math.floor(sec / 60);
          const s = sec % 60;
          return s > 0 ? `${m}m ${s}s` : `${m}m`;
        };

        return (
        <Dialog open={!!selectedSubmission} onOpenChange={() => {
          setSelectedSubmission(null);
        }}>
          <DialogContent className="max-w-[calc(100vw-2rem)] sm:max-w-5xl max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-primary">Enquiry Details</DialogTitle>
              <DialogDescription>
                Submitted on {new Date(selectedSubmission.submittedAt).toLocaleString('en-US', { hour12: true })}
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-5">
              {/* Section 1: Lead Information */}
              <Card className="rounded-2xl border-dashed">
                <CardHeader className="pb-0">
                  <CardTitle className="text-base font-semibold text-primary">Lead Information</CardTitle>
                  <div className="border-t border-dashed border-border mt-3" />
                </CardHeader>
                <CardContent className="space-y-5 pt-5">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <InfoRow label="Full Name" value={selectedSubmission.fullName} />
                    <InfoRow
                      label="Email"
                      value={selectedSubmission.email}
                      href={`mailto:${selectedSubmission.email}`}
                      copyable
                    />
                    <InfoRow
                      label="Mobile Number"
                      value={selectedSubmission.mobileNumber}
                      href={`tel:${selectedSubmission.mobileNumber}`}
                      copyable
                    />
                    <InfoRow label="Purpose" value={selectedSubmission.purposeOfContact} />
                  </div>
                  {selectedSubmission.message && (
                    <div className="space-y-1.5">
                      <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Message</p>
                      <p className="text-sm text-foreground whitespace-pre-wrap leading-relaxed">
                        {selectedSubmission.message}
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Section 2: Lead Intelligence */}
              {intel && (
                <Card className="rounded-2xl border-dashed">
                  <CardHeader className="pb-0">
                    <CardTitle className="text-base font-semibold text-primary">Lead Intelligence</CardTitle>
                    <div className="border-t border-dashed border-border mt-3" />
                  </CardHeader>
                  <CardContent className="space-y-5 pt-5">
                    {/* Behavior summary */}
                    {session && (() => {
                      const hasInteraction = (session.songsPlayed?.length > 0) ||
                        (session.imagesViewed?.length > 0) ||
                        (session.eventsViewed?.length > 0) ||
                        (session.socialClicks && Object.keys(session.socialClicks).length > 0) ||
                        (session.streamingClicks && Object.keys(session.streamingClicks).length > 0);
                      const hasContact = session.eventBreakdown?.['contact_form_submit'] ||
                        (session.sectionFlow?.some(s => s.toLowerCase() === 'contact'));
                      if (!hasContact) return null;
                      return (
                        <p className="text-sm text-foreground font-semibold">
                          {hasInteraction
                            ? 'User explored content and reached out after engagement'
                            : 'User directly reached out'}
                        </p>
                      );
                    })()}

                    {/* Score ring + Tags row */}
                    <div className="flex flex-col sm:flex-row items-center sm:items-start gap-4 sm:gap-6">
                      <ScoreRing score={intentScore} size={110} />
                      <div className="flex-1 space-y-3 pt-1">
                        {/* Tags */}
                        <div className="flex flex-wrap items-center gap-2">
                          <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${
                            intentType === 'booking' ? 'bg-success/15 text-success' :
                            intentType === 'collaboration' ? 'bg-info/15 text-info' :
                            'bg-secondary text-muted-foreground'
                          }`}>
                            {intentType.charAt(0).toUpperCase() + intentType.slice(1)}
                          </span>
                          {intel.isRepeatLead && (
                            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-warning/15 text-warning">
                              <RotateCcw size={12} /> Repeat
                            </span>
                          )}
                        </div>
                        {/* Device + Location */}
                        <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
                          {intel.device && (
                            <span className="inline-flex items-center gap-1.5">
                              {intel.device === 'Mobile' ? <Smartphone size={14} /> : <Monitor size={14} />}
                              {intel.device}
                            </span>
                          )}
                          {intel.country && (
                            <span className="inline-flex items-center gap-1.5">
                              <MapPin size={14} />
                              {[intel.city, intel.country].filter(Boolean).join(', ')}
                            </span>
                          )}
                        </div>
                        {/* Signal badges */}
                        {intel.intent && intel.intent.signals.length > 0 && (
                          <div className="flex flex-wrap gap-2">
                            {intel.intent.signals.map((signal, i) => (
                              <span key={i} className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-secondary text-secondary-foreground">
                                {signal}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Stats line */}
                    {session && session.totalEvents > 0 && (
                      <div className="border-t border-dashed border-border pt-4">
                        <p className="text-sm text-muted-foreground flex items-center gap-1.5">
                          <Clock size={14} className="shrink-0" />
                          Spent {formatDuration(session.durationSeconds)} on site
                          {session.songsPlayed && session.songsPlayed.length > 0 && (() => {
                            const unique = [...new Set(session.songsPlayed)];
                            return <>&nbsp;· <Music size={14} className="shrink-0 ml-1" /> Played {unique.length} song{unique.length !== 1 ? 's' : ''}</>;
                          })()}
                          &nbsp;· <Activity size={14} className="shrink-0 ml-1" /> {session.totalEvents} interaction{session.totalEvents !== 1 ? 's' : ''}
                        </p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}

              {/* Section 3: Journey & Interaction */}
              {session && (session.sectionFlow?.length > 0 ||
                session.songsPlayed?.length > 0 ||
                session.imagesViewed?.length > 0 ||
                session.eventsViewed?.length > 0 ||
                (session.socialClicks && Object.keys(session.socialClicks).length > 0) ||
                (session.streamingClicks && Object.keys(session.streamingClicks).length > 0)) && (
                <Card className="rounded-2xl border-dashed">
                  <CardHeader className="pb-0">
                    <CardTitle className="text-base font-semibold text-primary">Journey & Interaction</CardTitle>
                    <div className="border-t border-dashed border-border mt-3" />
                  </CardHeader>
                  <CardContent className="space-y-5 pt-5">
                    {/* Journey flow */}
                    {session.sectionFlow && session.sectionFlow.length > 0 && (
                      <JourneyFlow sections={session.sectionFlow} />
                    )}

                    {/* Content Accessed */}
                    {(session.songsPlayed?.length > 0 ||
                      session.imagesViewed?.length > 0 ||
                      session.eventsViewed?.length > 0 ||
                      (session.socialClicks && Object.keys(session.socialClicks).length > 0) ||
                      (session.streamingClicks && Object.keys(session.streamingClicks).length > 0)) && (
                      <div className="space-y-3">
                        <p className="text-sm font-semibold text-foreground">Content Accessed</p>
                        <InteractionList
                          songsPlayed={session.songsPlayed}
                          imagesViewed={session.imagesViewed}
                          eventsViewed={session.eventsViewed}
                          socialClicks={session.socialClicks}
                          streamingClicks={session.streamingClicks}
                        />
                      </div>
                    )}

                    {/* Engagement badge */}
                    <div className="border-t border-dashed border-border pt-4">
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-success/15 text-success">
                        Highly engaged visitor
                      </span>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Actions */}
              <div className="flex flex-col-reverse sm:flex-row gap-3 sm:justify-end border-t border-border pt-4">
                <Button
                  variant="outline"
                  onClick={() => handleToggleImportant(selectedSubmission._id)}
                  loading={togglingImportantId === selectedSubmission._id}
                  loadingText={selectedSubmission.isImportant ? 'Unmarking...' : 'Marking...'}
                  className="flex items-center justify-center gap-2"
                >
                  <Star size={16} className={selectedSubmission.isImportant ? 'fill-warning text-warning' : ''} />
                  {selectedSubmission.isImportant ? 'Unmark Important' : 'Mark as Important'}
                </Button>
                <Button variant="outline" onClick={() => { setSelectedSubmission(null); }}>
                  Close
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
        );
      })()}

      {/* Delete Confirmation Modal */}
      <ConfirmDialog
        open={!!deleteId}
        onOpenChange={(open) => !open && setDeleteId(null)}
        title="Delete Enquiry?"
        description="This action cannot be undone. The enquiry will be permanently deleted."
        confirmLabel="Delete"
        onConfirm={handleDelete}
      />
    </div>
  );
};

export default ContactWebsiteManager;
